import { useEffect, useState, useCallback, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import {Plus, Trash2, Loader2, Upload, X, Award, GripVertical, Globe, Eye, EyeOff} from 'lucide-react'
import toast from 'react-hot-toast'
import type { Partner } from '@/types'
import { useAuth, canEdit, canDelete } from '@/hooks/useAuth'

const TEAL = '#5BBDB5'
const TEAL_BG = '#DCF0EE'

export default function ManagePartners() {
  const { user } = useAuth()
  const allowEdit   = canEdit(user, 'parceiros')
  const allowDelete = canDelete(user, 'parceiros')
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingId, setUploadingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [newLogoFile, setNewLogoFile] = useState<File | null>(null)
  const [newLogoPreview, setNewLogoPreview] = useState<string>('')
  const [form, setForm] = useState({ name: '', website: '' })

  const fetchPartners = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.partners.list({
        sort: { order: 1 },
        limit: 50,
      })
      setPartners(list as Partner[])
    } catch (e) {
      console.error('Erro ao carregar parceiros:', e)
      toast.error('Erro ao carregar parceiros.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchPartners() }, [fetchPartners])

  const handleLogoSelect = (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('Apenas imagens.'); return }
    setNewLogoFile(file)
    setNewLogoPreview(URL.createObjectURL(file))
  }

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Informe o nome do parceiro.'); return }
    setSaving(true)
    try {
      let logoUrl = ''
      if (newLogoFile) {
        const result = await lumi.tools.file.upload([newLogoFile])
        logoUrl = result?.[0]?.fileUrl || result?.[0]?.url || ''
      }
      const now = new Date().toISOString()
      await lumi.entities.partners.create({
        name: form.name.trim(),
        website: form.website.trim() || '',
        logoUrl,
        isActive: true,
        order: partners.length + 1,
        creator: user?.userId || '',
        createdAt: now,
        updatedAt: now,
      })
      toast.success('Parceiro adicionado!')
      setForm({ name: '', website: '' })
      setNewLogoFile(null)
      setNewLogoPreview('')
      setShowForm(false)
      await fetchPartners()
    } catch (e) {
      console.error(e)
      toast.error('Erro ao adicionar parceiro.')
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (partner: Partner, file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('Apenas imagens.'); return }
    setUploadingId(partner._id)
    try {
      const result = await lumi.tools.file.upload([file])
      const logoUrl = result?.[0]?.fileUrl || result?.[0]?.url || ''
      await lumi.entities.partners.update(partner._id, { logoUrl, updatedAt: new Date().toISOString() })
      toast.success('Logo atualizado!')
      await fetchPartners()
    } catch {
      toast.error('Erro ao fazer upload.')
    } finally {
      setUploadingId(null)
    }
  }

  const handleToggleActive = async (partner: Partner) => {
    try {
      await lumi.entities.partners.update(partner._id, {
        isActive: !partner.isActive,
        updatedAt: new Date().toISOString(),
      })
      toast.success(partner.isActive ? 'Parceiro ocultado.' : 'Parceiro ativado.')
      await fetchPartners()
    } catch {
      toast.error('Erro ao atualizar.')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Excluir este parceiro?')) return
    try {
      await lumi.entities.partners.delete(id)
      toast.success('Parceiro excluído.')
      await fetchPartners()
    } catch {
      toast.error('Erro ao excluir.')
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Nossos Parceiros</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gerencie os parceiros exibidos no carrossel da landing page.
          </p>
        </div>
        {allowEdit && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all shadow-sm"
            style={{ background: TEAL }}
          >
            <Plus size={16} />
            Novo Parceiro
          </button>
        )}
      </div>

      {/* Formulário de criação */}
      {showForm && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 mb-6">
          <h2 className="font-bold text-gray-900 dark:text-white mb-4">Adicionar Parceiro</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Nome *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Prefeitura Municipal"
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                onFocus={e => (e.currentTarget.style.boxShadow = '0 0 0 2px rgba(91,189,181,0.35)')}
                onBlur={e => (e.currentTarget.style.boxShadow = '')}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Site (opcional)</label>
              <input
                type="url"
                value={form.website}
                onChange={e => setForm(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://..."
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                onFocus={e => (e.currentTarget.style.boxShadow = '0 0 0 2px rgba(91,189,181,0.35)')}
                onBlur={e => (e.currentTarget.style.boxShadow = '')}
              />
            </div>
          </div>

          {/* Upload logo */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Logo do Parceiro</label>
            {newLogoPreview ? (
              <div className="relative inline-block">
                <img src={newLogoPreview} alt="Preview" className="h-16 w-auto object-contain rounded-lg border border-gray-200 p-2 bg-gray-50" />
                <button
                  onClick={() => { setNewLogoFile(null); setNewLogoPreview('') }}
                  className="absolute -top-1.5 -right-1.5 p-1 bg-red-600 text-white rounded-full shadow"
                >
                  <X size={10} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => logoInputRef.current?.click()}
                className="w-32 h-20 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-1 cursor-pointer transition-all"
                style={{ borderColor: '#8DD0CC' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = TEAL; e.currentTarget.style.background = TEAL_BG }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#8DD0CC'; e.currentTarget.style.background = '' }}
              >
                <Upload size={18} style={{ color: TEAL }} />
                <span className="text-xs text-gray-400">Upload logo</span>
              </div>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoSelect(f); e.target.value = '' }}
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCreate}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50"
              style={{ background: TEAL }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Adicionar
            </button>
            <button
              onClick={() => { setShowForm(false); setForm({ name: '', website: '' }); setNewLogoFile(null); setNewLogoPreview('') }}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin" style={{ color: TEAL }} />
        </div>
      ) : partners.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800">
          <Award size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400 text-sm">Nenhum parceiro cadastrado ainda.</p>
        {allowEdit && (
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 px-5 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: TEAL }}
          >
            Adicionar o primeiro parceiro
          </button>
        )}
        </div>
      ) : (
        <div className="space-y-3">
          {partners.map(partner => {
            const fileRef = { current: null as HTMLInputElement | null }
            return (
              <div
                key={partner._id}
                className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 flex items-center gap-4"
                style={{ opacity: partner.isActive === false ? 0.6 : 1 }}
              >
                <GripVertical size={16} className="text-gray-300 flex-shrink-0 cursor-grab" />

                {/* Logo */}
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden relative group cursor-pointer"
                  style={{ background: TEAL_BG }}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = 'image/*'
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0]
                      if (file) handleLogoUpload(partner, file)
                    }
                    input.click()
                  }}
                >
                  {partner.logoUrl ? (
                    <>
                      <img src={partner.logoUrl} alt={partner.name} className="w-full h-full object-contain p-2" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        {uploadingId === partner._id ? <Loader2 size={16} className="animate-spin text-white" /> : <Upload size={16} className="text-white" />}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      {uploadingId === partner._id ? <Loader2 size={20} className="animate-spin" style={{ color: TEAL }} /> : <Upload size={20} style={{ color: TEAL }} />}
                      <span className="text-xs text-gray-400">Logo</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 dark:text-white text-sm">{partner.name}</div>
                  {partner.website && (
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-gray-400 hover:underline mt-0.5"
                    >
                      <Globe size={11} />
                      {partner.website}
                    </a>
                  )}
                  <span
                    className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={partner.isActive !== false
                      ? { background: '#dcfce7', color: '#166534' }
                      : { background: '#f3f4f6', color: '#9ca3af' }
                    }
                  >
                    {partner.isActive !== false ? 'Ativo' : 'Oculto'}
                  </span>
                </div>

                {/* Ações */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {allowEdit && (
                    <button
                      onClick={() => handleToggleActive(partner)}
                      className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                      title={partner.isActive !== false ? 'Ocultar do site' : 'Exibir no site'}
                    >
                      {partner.isActive !== false ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  )}
                  {allowDelete && (
                    <button
                      onClick={() => handleDelete(partner._id)}
                      className="p-2 rounded-lg text-red-400 hover:bg-red-50 transition-all"
                      title="Excluir"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
